import IconButton from 'components/@extended/IconButton';
import { Tooltip } from 'components/ui/tooltip';
import { CopiedStatus, CopyGlyph } from 'components/util/CopiedStatus';
import { useEffect, useRef, useState } from 'react';
import { HourlyRate } from 'types/pricelists';
import { t } from 'utils/getTranslations';
import { buildHourlyRatesClipboardData, ClipboardRateRow } from './util';

interface Props {
    // The role groups to copy; unset while a card that fetches its rows lazily has not received them yet.
    data?: Record<string, HourlyRate[]>;
    // Fetches the role groups for a press that comes before `data` has arrived.
    loadData?: () => Promise<Record<string, HourlyRate[]>>;
    groupTitles: Record<string, string>;
    // Groups beyond the role groups, such as a case list's personal rows or an employee-priced list's people.
    extraGroups?: Record<string, ClipboardRateRow[]>;
    // Fetches the extra groups for a press that comes before `extraGroups` has arrived.
    loadExtraGroups?: () => Promise<Record<string, ClipboardRateRow[]>>;
}

export const CopyHourlyRatesToClipboard = ({
    data,
    loadData,
    groupTitles,
    extraGroups,
    loadExtraGroups,
}: Props): JSX.Element => {
    const label = t('copy-to-clipboard');
    // A copy swaps the glyph and keeps the label; a failure says so in the tooltip and the name.
    const [outcome, setOutcome] = useState<'copied' | 'failed' | null>(null);
    const tooltipText = outcome === 'failed' ? t('copy-to-clipboard-failed') : label;
    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const showForAMoment = (next: 'copied' | 'failed') => {
        setOutcome(next);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => setOutcome(null), 1500);
    };

    const handleClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            // Rows already at hand are copied without an await, which Safari needs to keep the click's
            // clipboard permission.
            const rows = data ?? (loadData ? await loadData() : {});
            const extra = extraGroups ?? (loadExtraGroups ? await loadExtraGroups() : undefined);
            const { html, plainText } = buildHourlyRatesClipboardData(rows, groupTitles, extra);
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' }),
                    }),
                ]);
            } catch {
                // Fallback to plain text
                await navigator.clipboard.writeText(plainText);
            }
            showForAMoment('copied');
        } catch {
            showForAMoment('failed');
        }
    };

    return (
        <>
            <Tooltip content={tooltipText} closeOnClick={false}>
                <IconButton
                    aria-label={tooltipText}
                    className="text-fg-secondary hover:text-fg size-6 shrink-0"
                    onClick={(e) => {
                        void handleClick(e);
                    }}
                >
                    <CopyGlyph copied={outcome === 'copied'} />
                </IconButton>
            </Tooltip>
            <CopiedStatus copied={outcome === 'copied'} failed={outcome === 'failed'} />
        </>
    );
};
